import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  InsightsTicketFollowthroughResponse,
  TicketFollowthroughCounts,
  TicketFollowthroughGroup,
  TicketFollowthroughGroupRow,
  TicketFollowthroughRoles,
  TicketFollowthroughWindow,
} from "@shared-core/data/api-types";
import {
  TICKET_FOLLOWTHROUGH_ROLE_LABELS,
  TICKET_FOLLOWTHROUGH_ROLE_OPTIONS,
  TICKET_FOLLOWTHROUGH_WINDOW_LABELS,
  TICKET_FOLLOWTHROUGH_WINDOW_OPTIONS,
  TICKET_GROUP_LABELS,
  formatFollowthroughPct,
  formatHoursToFirstAction,
  ticketTypeLabel,
} from "@shared-core/domain/insights-ticket-followthrough";
import { roleLabel } from "@shared-core/domain/roleLabels";
import { formatCount } from "@shared-core/domain/formatters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";

// Insights → Ticket follow-through: are tickets being worked? Mostly for the
// marketing manager (are campaigns being followed up?), and for spotting
// owners who work one kind of ticket but not another. Over the composed
// /views/insights/ticket-followthrough endpoint (dbt ticket_followthrough,
// hourly): tiles, a breakdown by group → type → campaign / landing page whose
// rows narrow the tiles and owner table, and the owner table. Opens on
// marketing. Every control refetches; nothing is filtered client-side. Names
// staff, so gated on the management insights permit like Product usage.

interface Selection {
  ticketGroup: TicketFollowthroughGroup;
  subjectType: string | null;
  campaignId: string | null;
  leadSourceId: string | null;
}

const MARKETING: Selection = {
  ticketGroup: "marketing",
  subjectType: null,
  campaignId: null,
  leadSourceId: null,
};
const ALL: Selection = { ...MARKETING, ticketGroup: "all" };

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; followthrough: InsightsTicketFollowthroughResponse };

function sameSelection(a: Selection, b: Selection) {
  return (
    a.ticketGroup === b.ticketGroup &&
    a.subjectType === b.subjectType &&
    a.campaignId === b.campaignId &&
    a.leadSourceId === b.leadSourceId
  );
}

/** "Marketing › Engagement follow-up › Big Pink Book" for the active slice. */
function selectionLabel(
  selection: Selection,
  breakdown: TicketFollowthroughGroupRow[],
): string {
  if (selection.ticketGroup === "all") return "All tickets";
  const parts = [TICKET_GROUP_LABELS[selection.ticketGroup]];
  if (selection.subjectType !== null) {
    parts.push(ticketTypeLabel(selection.subjectType));
  }
  if (selection.campaignId || selection.leadSourceId) {
    const source = breakdown
      .flatMap((g) => g.types)
      .flatMap((t) => t.sources ?? [])
      .find(
        (s) =>
          (selection.campaignId && s.campaign_id === selection.campaignId) ||
          (selection.leadSourceId &&
            s.lead_source_id === selection.leadSourceId),
      );
    parts.push(source?.source_name ?? "Unnamed");
  }
  return parts.join(" › ");
}

export function TicketFollowthroughTab() {
  const adapter = useDataAdapter();
  const [selectedWindow, setSelectedWindow] =
    useState<TicketFollowthroughWindow>("last_90_days");
  const [roles, setRoles] = useState<TicketFollowthroughRoles>("sales_team");
  const [selection, setSelection] = useState<Selection>(MARKETING);
  const [state, setState] = useState<State>({ status: "loading" });
  const breakdownRef = useRef<HTMLDivElement>(null);
  // Which breakdown rows are open lives here, not in the breakdown, so a
  // selection's refetch doesn't fold away the rows the user just opened.
  // Marketing starts open: it is what the page is mostly for.
  const [openRows, setOpenRows] = useState<Set<string>>(
    () => new Set(["marketing"]),
  );
  const toggleRow = (key: string) =>
    setOpenRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const load = useCallback(async () => {
    // Keep the current figures on screen while a new selection loads; only
    // the first load (or a retry after an error) shows the loading panel.
    setState((current) =>
      current.status === "ready" ? current : { status: "loading" },
    );
    try {
      const followthrough = await adapter.getInsightsTicketFollowthrough({
        window: selectedWindow,
        roles,
        ...selection,
      });
      setState({ status: "ready", followthrough });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, selectedWindow, roles, selection]);

  useEffect(() => {
    load();
  }, [load]);

  const showBreakdown = () =>
    breakdownRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={selectedWindow}
          onValueChange={(v) =>
            setSelectedWindow(v as TicketFollowthroughWindow)
          }
        >
          <SelectTrigger className="w-56" aria-label="Window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TICKET_FOLLOWTHROUGH_WINDOW_OPTIONS.map((w) => (
              <SelectItem key={w} value={w}>
                {TICKET_FOLLOWTHROUGH_WINDOW_LABELS[w]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={roles}
          onValueChange={(v) => setRoles(v as TicketFollowthroughRoles)}
        >
          <SelectTrigger className="w-56" aria-label="Whose tickets">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TICKET_FOLLOWTHROUGH_ROLE_OPTIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {TICKET_FOLLOWTHROUGH_ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground ml-auto text-xs">
          Updates hourly
        </span>
      </div>

      {state.status === "loading" && (
        <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
          Loading ticket follow-through…
        </div>
      )}
      {state.status === "error" && (
        <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
          <p className="text-destructive text-sm">
            Couldn’t load ticket follow-through. Please try again.
          </p>
          <button
            type="button"
            onClick={load}
            className="text-primary text-sm underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
      {state.status === "ready" && (
        <div className="space-y-8">
          <SelectionBar
            label={selectionLabel(selection, state.followthrough.breakdown)}
            isAll={selection.ticketGroup === "all"}
            onShowAll={() => setSelection(ALL)}
          />
          {state.followthrough.total.tickets === 0 ? (
            <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
              No tickets for this selection yet.
            </div>
          ) : (
            <TotalsSection
              total={state.followthrough.total}
              onShowBreakdown={showBreakdown}
            />
          )}
          <div ref={breakdownRef}>
            <BreakdownSection
              breakdown={state.followthrough.breakdown}
              openRows={openRows}
              onToggleRow={toggleRow}
              selection={selection}
              onSelect={(next) =>
                setSelection(sameSelection(next, selection) ? ALL : next)
              }
            />
          </div>
          {state.followthrough.total.tickets > 0 && (
            <OwnerTableSection
              followthrough={state.followthrough}
              showGroupSplit={selection.ticketGroup === "all"}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SelectionBar({
  label,
  isAll,
  onShowAll,
}: {
  label: string;
  isAll: boolean;
  onShowAll: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Showing</span>
      {isAll ? (
        <span className="font-medium">All tickets</span>
      ) : (
        <button
          type="button"
          onClick={onShowAll}
          aria-label={`Clear selection: ${label}`}
          className="bg-accent text-accent-foreground inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium"
        >
          {label}
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

function TotalsSection({
  total,
  onShowBreakdown,
}: {
  total: TicketFollowthroughCounts;
  onShowBreakdown: () => void;
}) {
  const tiles: { label: string; value: string }[] = [
    { label: "Opened", value: formatFollowthroughPct(total.opened_pct) },
    { label: "Actioned", value: formatFollowthroughPct(total.actioned_pct) },
    {
      label: "Closed by the owner",
      value: formatFollowthroughPct(total.closed_by_person_pct),
    },
    { label: "Still open", value: formatCount(total.still_open) },
  ];
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {formatCount(total.tickets)} tickets. Click a figure to see it by ticket
        type.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={onShowBreakdown}
            className="border-border bg-surface hover:border-foreground/30 rounded-lg border p-4 text-left transition-colors"
          >
            <div className="text-muted-foreground text-sm">{tile.label}</div>
            <div className="text-foreground mt-2 text-2xl font-semibold">
              {tile.value}
            </div>
          </button>
        ))}
      </div>
      <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs">
        <li>
          <strong className="font-medium">Opened:</strong> the ticket has been
          opened at least once, by anyone.
        </li>
        <li>
          <strong className="font-medium">Actioned:</strong> someone commented
          on it or changed its status, priority or due date. Assigning it
          doesn’t count.
        </li>
        <li>
          <strong className="font-medium">Closed by the owner:</strong> leaves
          out tickets the system closed automatically. Including those,{" "}
          {formatFollowthroughPct(total.closed_any_pct)} are closed.
        </li>
      </ul>
    </div>
  );
}

function CountCells({ row }: { row: TicketFollowthroughCounts }) {
  return (
    <>
      <TableCell className="text-right">{formatCount(row.tickets)}</TableCell>
      <TableCell className="text-right">
        {formatFollowthroughPct(row.actioned_pct)}
      </TableCell>
      <TableCell className="text-right">
        {formatFollowthroughPct(row.closed_by_person_pct)}
      </TableCell>
      <TableCell className="text-right">
        {formatCount(row.still_open)}
      </TableCell>
    </>
  );
}

function BreakdownSection({
  breakdown,
  openRows,
  onToggleRow,
  selection,
  onSelect,
}: {
  breakdown: TicketFollowthroughGroupRow[];
  openRows: Set<string>;
  onToggleRow: (key: string) => void;
  selection: Selection;
  onSelect: (selection: Selection) => void;
}) {
  if (breakdown.length === 0) return null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          By ticket type
        </h2>
        <p className="text-muted-foreground text-sm">
          Click a row to show only those tickets above and below; click it again
          to show everything. Marketing types open up into campaigns and landing
          pages.
        </p>
      </div>
      <div className="border-border bg-surface overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Tickets</TableHead>
              <TableHead className="text-right">Actioned</TableHead>
              <TableHead className="text-right">Closed by the owner</TableHead>
              <TableHead className="text-right">Still open</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {breakdown.map((group) => {
              const groupKey = group.ticket_group;
              const groupSelection: Selection = {
                ...MARKETING,
                ticketGroup: groupKey as TicketFollowthroughGroup,
              };
              const isGroupOpen = openRows.has(groupKey);
              return [
                <SelectableRow
                  key={groupKey}
                  isSelected={sameSelection(selection, groupSelection)}
                  onSelect={() => onSelect(groupSelection)}
                  className="bg-muted/40 font-medium"
                  label={
                    TICKET_GROUP_LABELS[
                      groupKey as keyof typeof TICKET_GROUP_LABELS
                    ] ?? groupKey
                  }
                  expander={{
                    isOpen: isGroupOpen,
                    onToggle: () => onToggleRow(groupKey),
                  }}
                  row={group}
                  indent={0}
                />,
                ...(isGroupOpen
                  ? (group.types ?? []).flatMap((type) => {
                      const typeKey = `${groupKey}:${type.subject_type ?? ""}`;
                      const typeSelection: Selection = {
                        ...groupSelection,
                        subjectType: type.subject_type ?? null,
                      };
                      const sources = type.sources ?? [];
                      const isTypeOpen = openRows.has(typeKey);
                      return [
                        <SelectableRow
                          key={typeKey}
                          isSelected={sameSelection(selection, typeSelection)}
                          onSelect={() => onSelect(typeSelection)}
                          label={ticketTypeLabel(type.subject_type)}
                          expander={
                            sources.length > 0
                              ? {
                                  isOpen: isTypeOpen,
                                  onToggle: () => onToggleRow(typeKey),
                                }
                              : null
                          }
                          row={type}
                          indent={1}
                        />,
                        ...(isTypeOpen
                          ? sources.map((source) => {
                              const sourceSelection: Selection = {
                                ...typeSelection,
                                campaignId: source.campaign_id ?? null,
                                leadSourceId: source.lead_source_id ?? null,
                              };
                              return (
                                <SelectableRow
                                  key={`${typeKey}:${source.campaign_id ?? source.lead_source_id}`}
                                  isSelected={sameSelection(
                                    selection,
                                    sourceSelection,
                                  )}
                                  onSelect={() => onSelect(sourceSelection)}
                                  label={source.source_name ?? "Unnamed"}
                                  className="text-muted-foreground"
                                  expander={null}
                                  row={source}
                                  indent={2}
                                />
                              );
                            })
                          : []),
                      ];
                    })
                  : []),
              ];
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SelectableRow({
  label,
  row,
  indent,
  isSelected,
  onSelect,
  expander,
  className = "",
}: {
  label: string;
  row: TicketFollowthroughCounts;
  indent: 0 | 1 | 2;
  isSelected: boolean;
  onSelect: () => void;
  expander: { isOpen: boolean; onToggle: () => void } | null;
  className?: string;
}) {
  const padding = ["pl-2", "pl-8", "pl-14"][indent];
  return (
    <TableRow
      aria-selected={isSelected}
      onClick={onSelect}
      // Selected wins over the row's own background (group rows are shaded).
      className={`cursor-pointer ${isSelected ? "bg-accent font-medium" : className}`}
    >
      <TableCell className={padding}>
        <div className="flex items-center gap-1.5">
          {expander ? (
            <button
              type="button"
              aria-expanded={expander.isOpen}
              aria-label={`${expander.isOpen ? "Hide" : "Show"} breakdown of ${label}`}
              className="text-muted-foreground hover:text-foreground -ml-1 rounded p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                expander.onToggle();
              }}
            >
              {expander.isOpen ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </button>
          ) : (
            <span className="inline-block w-5" />
          )}
          <span>{label}</span>
        </div>
      </TableCell>
      <CountCells row={row} />
    </TableRow>
  );
}

function PctCell({
  pct,
  count,
}: {
  pct: number | null | undefined;
  count: number;
}) {
  return (
    <TableCell className="text-right">
      <div>{formatFollowthroughPct(pct)}</div>
      <div className="text-muted-foreground text-xs">{formatCount(count)}</div>
    </TableCell>
  );
}

function OwnerTableSection({
  followthrough,
  showGroupSplit,
}: {
  followthrough: InsightsTicketFollowthroughResponse;
  showGroupSplit: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">By owner</h2>
        <p className="text-muted-foreground text-sm">
          {showGroupSplit
            ? "Marketing and Customer alerts show the share of each kind of ticket the owner has actioned."
            : "Choose All tickets to compare marketing with customer alerts side by side."}{" "}
          Sorted by share of tickets actioned. A ticket being covered for
          someone away counts for whoever is covering it.
        </p>
      </div>
      <div className="border-border bg-surface overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Tickets</TableHead>
              <TableHead className="text-right">Opened</TableHead>
              <TableHead className="text-right">Actioned</TableHead>
              {showGroupSplit && (
                <>
                  <TableHead className="text-right">Marketing</TableHead>
                  <TableHead className="text-right">Customer alerts</TableHead>
                </>
              )}
              <TableHead className="text-right">Closed by the owner</TableHead>
              <TableHead className="text-right">Still open</TableHead>
              <TableHead className="text-right">Time to first action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {followthrough.reps.map((rep) => (
              <TableRow key={rep.user_id ?? "unassigned"}>
                <TableCell>
                  <div className="font-medium">
                    {rep.user_id === null || rep.user_id === undefined
                      ? "Unassigned"
                      : (rep.user_name ?? "Unknown user")}
                  </div>
                  {rep.role && (
                    <div className="text-muted-foreground text-xs">
                      {roleLabel(rep.role)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {formatCount(rep.tickets)}
                </TableCell>
                <PctCell pct={rep.opened_pct} count={rep.opened} />
                <PctCell pct={rep.actioned_pct} count={rep.actioned} />
                {showGroupSplit && (
                  <>
                    <PctCell
                      pct={rep.marketing_actioned_pct}
                      count={rep.marketing_tickets ?? 0}
                    />
                    <PctCell
                      pct={rep.customer_alerts_actioned_pct}
                      count={rep.customer_alerts_tickets ?? 0}
                    />
                  </>
                )}
                <PctCell
                  pct={rep.closed_by_person_pct}
                  count={rep.closed_by_person}
                />
                <TableCell className="text-right">
                  {formatCount(rep.still_open)}
                </TableCell>
                <TableCell className="text-right">
                  {formatHoursToFirstAction(rep.median_hours_to_first_action)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-muted-foreground text-xs">
        {showGroupSplit &&
          "Under Marketing and Customer alerts, the small figure is how many of the owner's tickets are of that kind. "}
        Time to first action is the median, across the tickets the owner did
        action, from the ticket being raised to their first action on it.
      </p>
    </div>
  );
}
